import React, { useEffect, useMemo, useState } from "react";
import { PrivateFileAnchor } from "../../../../utils/privateFile";
import { Switch, FormControlLabel } from "@material-ui/core";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import {
	InputOrderAdminTitle,
	UserFormServiceButtonClose,
	UserFormServiceButtonCreated,
} from "../../MyBooking/styled/DesStyled";
import { ErrorNotiLogin, FomrmLoginInput } from "../../LoginUser/styled/LoginStyles";
import Partner from "../../../../models/Partner";
import useAlertDialog from "../../../hooks/useAlertDialog";
import { AlertType } from "../../../hooks/common/useAttachAlertDialog";
import {
	PutPartnerAdminFinancialFields,
	adjustPartnerAvailable,
	getAdjustPartnerAvailableErrorMessage,
	getPartnerAvailableSummary,
	getPartnerBalanceAdjustments,
	PartnerBalanceAdjustment,
	uploadPartnerBalanceAdjustmentFiles,
} from "../../../../services/PartnerService";
import {
	formatMoneyInputDisplay,
	formatValueToDisplay,
	parseMoneyToNumber,
	sanitizeMoneyInput,
} from "./moneyInput";

function validateMoneyValue(value: string, isOptional: boolean = true) {
	if (value === "") return isOptional ? null : "Required";
	if (value.endsWith(".")) return null;
	const num = parseMoneyToNumber(value);
	if (num === null) return "Invalid amount";
	return null;
}

function isCompleteMoneyValue(value: string, isOptional: boolean = true) {
	if (value === "") return isOptional;
	if (value.endsWith(".")) return false;
	return parseMoneyToNumber(value) !== null;
}

const MAX_ATTACHMENT_FILES = 10;
const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;

function formatFileSize(size: number) {
	if (size >= 1024 * 1024) {
		return `${(size / 1024 / 1024).toFixed(2)} MB`;
	}
	return `${Math.round(size / 1024)} KB`;
}

function getAttachmentDisplayLabel(attachmentUrl: string, index: number) {
	const cleanUrl = String(attachmentUrl || "").split("?")[0];
	const filename = cleanUrl.split("/").filter(Boolean).pop() || "";
	const extension = filename.includes(".") ? filename.split(".").pop()?.toUpperCase() : "";

	return extension ? `Open ${extension} ${index + 1}` : `Open file ${index + 1}`;
}

export default function DialogUpdatePartnerFinancialFields(props: {
	openDialog: boolean;
	onClose: () => void;
	partner: Partner;
	onSuccess?: () => void;
}) {
	const { openAlertDialog } = useAlertDialog();
	const moneyFormatter = new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

	const [stationCom, setStationCom] = useState("");
	const [minimumPayout, setMinimumPayout] = useState("");
	const [maximumPayout, setMaximumPayout] = useState("");
	const [pubRate, setPubRate] = useState("");
	const [minBalance, setMinBalance] = useState("");
	const [minBalanceEnabled, setMinBalanceEnabled] = useState(true);
	const [minimumPayoutEnabled, setMinimumPayoutEnabled] = useState(true);
	const [initialMinPayoutEnabled, setInitialMinPayoutEnabled] = useState(true);

	const [availableSummary, setAvailableSummary] = useState<any>(null);
	const [adjustAmount, setAdjustAmount] = useState("");
	const [adjustType, setAdjustType] = useState<"add" | "subtract">("add");
	const [adjustReason, setAdjustReason] = useState("");
	const [adjustFiles, setAdjustFiles] = useState<File[]>([]);
	const [adjustFilesError, setAdjustFilesError] = useState("");
	const [hideFromStation, setHideFromStation] = useState(false);
	const [adjustHistory, setAdjustHistory] = useState<PartnerBalanceAdjustment[]>([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (props.openDialog && props.partner) {
			setStationCom(formatValueToDisplay(props.partner.stationCom));
			setMinimumPayout(formatValueToDisplay(props.partner.minimumPayout));
			setMaximumPayout(formatValueToDisplay(props.partner.maximumPayout));
			setPubRate(formatValueToDisplay(props.partner.pubRate));
			setMinBalance(formatValueToDisplay(props.partner.minBalance));
			setMinBalanceEnabled(props.partner.minBalanceEnabled !== false);
			setMinimumPayoutEnabled(props.partner.minimumPayoutEnabled !== false);
			setInitialMinPayoutEnabled(props.partner.minimumPayoutEnabled !== false);
			setAdjustAmount("");
			setAdjustReason("");
			setAdjustFiles([]);
			setAdjustFilesError("");
			setHideFromStation(false);
			void Promise.all([fetchSummary(), fetchBalanceAdjustments()]);
		}
	}, [props.openDialog, props.partner]);

	const handleAdjustmentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(e.target.files || []);
		if (!selectedFiles.length) return;

		const tooLarge = selectedFiles.find((file) => file.size > MAX_ATTACHMENT_SIZE);
		if (tooLarge) {
			setAdjustFilesError(
				`File "${tooLarge.name}" exceeds ${(MAX_ATTACHMENT_SIZE / 1024 / 1024).toFixed(0)} MB.`,
			);
			e.target.value = "";
			return;
		}

		const nextFiles = [...adjustFiles, ...selectedFiles].slice(0, MAX_ATTACHMENT_FILES);
		if (adjustFiles.length + selectedFiles.length > MAX_ATTACHMENT_FILES) {
			setAdjustFilesError(`You can upload up to ${MAX_ATTACHMENT_FILES} files.`);
		} else {
			setAdjustFilesError("");
		}

		setAdjustFiles(nextFiles);
		e.target.value = "";
	};

	const removeAdjustmentFile = (index: number) => {
		setAdjustFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
		setAdjustFilesError("");
	};

	const fetchSummary = async () => {
		if (!props.partner.id) return;
		try {
			const res = await getPartnerAvailableSummary(props.partner.id);
			if (typeof res.summary?.minimumPayoutEnabled === "boolean") {
				setMinimumPayoutEnabled(res.summary.minimumPayoutEnabled);
				setInitialMinPayoutEnabled(res.summary.minimumPayoutEnabled);
			}
			setAvailableSummary({
				...(res.summary || {}),
				paidAmount: res.paidAmount ?? props.partner.paidAmount ?? 0,
				paidAmountLocal: res.paidAmountLocal ?? props.partner.paidAmountLocal ?? 0,
				availableDeductionAmount:
					res.summary?.availableDeductionAmount ??
					res.availableDeductionAmount ??
					props.partner.availableDeductionAmount ??
					0,
			});
		} catch (err) {
			console.error(err);
		}
	};

	const fetchBalanceAdjustments = async () => {
		if (!props.partner.id) return;
		try {
			setHistoryLoading(true);
			const res = await getPartnerBalanceAdjustments(props.partner.id, 1, 20);
			setAdjustHistory(res.docs || []);
		} catch (err) {
			console.error(err);
			setAdjustHistory([]);
		} finally {
			setHistoryLoading(false);
		}
	};

	const errors = useMemo(() => {
		let stationComErr = validateMoneyValue(stationCom);
		let minimumPayoutErr = validateMoneyValue(minimumPayout);
		let maximumPayoutErr = validateMoneyValue(maximumPayout);
		let pubRateErr = validateMoneyValue(pubRate);
		let minBalanceErr = validateMoneyValue(minBalance);

		if (!minimumPayoutErr && !maximumPayoutErr && minimumPayout !== "" && maximumPayout !== "") {
			const min = parseMoneyToNumber(minimumPayout);
			const max = parseMoneyToNumber(maximumPayout);
			if (min !== null && max !== null && min > max) {
				minimumPayoutErr = "Min must be <= Max";
				maximumPayoutErr = "Max must be >= Min";
			}
		}

		return {
			stationCom: stationComErr,
			minimumPayout: minimumPayoutErr,
			maximumPayout: maximumPayoutErr,
			pubRate: pubRateErr,
			minBalance: minBalanceErr,
		};
	}, [stationCom, minimumPayout, maximumPayout, pubRate, minBalance]);

	const partnerInitialMinBalEnabled = props.partner.minBalanceEnabled !== false;
	// Xoá trắng ô đang có số = quay về mặc định (gửi null), khớp chú giải "Blank = default".
	const clearedMinPayout = minimumPayout === "" && props.partner.minimumPayout != null;
	const clearedMaxPayout = maximumPayout === "" && props.partner.maximumPayout != null;

	const canSave =
		!isSaving &&
		!errors.stationCom &&
		!errors.minimumPayout &&
		!errors.maximumPayout &&
		!errors.pubRate &&
		!errors.minBalance &&
		isCompleteMoneyValue(stationCom) &&
		isCompleteMoneyValue(minimumPayout) &&
		isCompleteMoneyValue(maximumPayout) &&
		isCompleteMoneyValue(pubRate) &&
		isCompleteMoneyValue(minBalance) &&
		(stationCom !== "" ||
			minimumPayout !== "" ||
			maximumPayout !== "" ||
			pubRate !== "" ||
			minBalance !== "" ||
			minBalanceEnabled !== partnerInitialMinBalEnabled ||
			minimumPayoutEnabled !== initialMinPayoutEnabled ||
			clearedMinPayout ||
			clearedMaxPayout);

	const handleSave = async () => {
		if (!canSave) return;
		try {
			setIsSaving(true);
			const data: {
				stationCom?: number | null;
				minimumPayout?: number | null;
				maximumPayout?: number | null;
				pubRate?: number | null;
				minBalance?: number | null;
				minBalanceEnabled?: boolean;
				minimumPayoutEnabled?: boolean;
			} = {};
			if (stationCom !== "") data.stationCom = parseMoneyToNumber(stationCom);
			if (minimumPayout !== "") data.minimumPayout = parseMoneyToNumber(minimumPayout);
			else if (clearedMinPayout) data.minimumPayout = null;
			if (maximumPayout !== "") data.maximumPayout = parseMoneyToNumber(maximumPayout);
			else if (clearedMaxPayout) data.maximumPayout = null;
			if (pubRate !== "") data.pubRate = parseMoneyToNumber(pubRate);
			data.minBalance = minBalance === "" ? null : parseMoneyToNumber(minBalance);
			data.minBalanceEnabled = minBalanceEnabled;
			if (minimumPayoutEnabled !== initialMinPayoutEnabled) data.minimumPayoutEnabled = minimumPayoutEnabled;
			await PutPartnerAdminFinancialFields(props.partner.id!, data);
			openAlertDialog?.(AlertType.Success, "Updated successfully!", () => {
				props.onSuccess?.();
				props.onClose();
			});
		} catch (err: any) {
			console.error(err);
			openAlertDialog?.(AlertType.Fail, err?.response?.data?.message || "Error updating financial fields");
		} finally {
			setIsSaving(false);
		}
	};

	const handleAdjust = async () => {
		const amount = parseMoneyToNumber(adjustAmount);
		const reason = adjustReason.trim();
		if (!amount || amount <= 0) {
			openAlertDialog?.(AlertType.Fail, "Please enter a valid amount");
			return;
		}
		if (!reason) {
			openAlertDialog?.(AlertType.Fail, "Please enter a reason for this adjustment");
			return;
		}
		try {
			setIsSaving(true);
			const attachmentUrls =
				adjustFiles.length > 0
					? await uploadPartnerBalanceAdjustmentFiles(adjustFiles)
					: [];
			await adjustPartnerAvailable(
				props.partner.id!,
				amount,
				adjustType,
				reason,
				attachmentUrls,
				currentAvailable,
				hideFromStation,
			);
			setAdjustAmount("");
			setAdjustReason("");
			setAdjustFiles([]);
			setAdjustFilesError("");
			setHideFromStation(false);
			await Promise.all([fetchSummary(), fetchBalanceAdjustments()]);
			props.onSuccess?.();
			openAlertDialog?.(AlertType.Success, "Balance adjusted successfully");
		} catch (err: any) {
			console.error(err);
			openAlertDialog?.(AlertType.Fail, getAdjustPartnerAvailableErrorMessage(err));
		} finally {
			setIsSaving(false);
		}
	};

	const summaryCurrency = availableSummary?.currencyCode || props.partner.localCurrencyCode || "VND";
	const formatSummaryMoney = (value?: number | null) => moneyFormatter.format(Number(value) || 0);
	const currentAvailable = Number(availableSummary?.availableAmount) || 0;
	const paidAmountDisplay = Number(
		availableSummary?.paidAmountLocal ?? availableSummary?.paidAmount
	) || 0;
	const pendingAdjustment = Number(
		availableSummary?.availableAdjustmentAmount ?? availableSummary?.availableDeductionAmount
	) || 0;
	const inTransitAmount = Number(availableSummary?.inTransitAmount) || 0;
	const withdrawableAfterReserve =
		availableSummary?.withdrawableAmount != null
			? Number(availableSummary.withdrawableAmount)
			: null;
	const reservedInAvailable =
		availableSummary?.reservedAmount != null
			? Number(availableSummary.reservedAmount)
			: null;
	const manualAmount = parseMoneyToNumber(adjustAmount) || 0;
	const projectedAvailable = +(currentAvailable + (adjustType === "add" ? manualAmount : -manualAmount)).toFixed(2);
	const canApplyAdjustment =
		!isSaving &&
		manualAmount > 0 &&
		adjustReason.trim().length > 0 &&
		!adjustFilesError;

	const formatHistoryDate = (value?: string) => {
		if (!value) return "-";
		return new Date(value).toLocaleString("en-GB", {
			year: "numeric",
			month: "short",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getOperationStyles = (operation: "add" | "subtract") =>
		operation === "add"
			? { bg: "#ECFDF5", color: "#047857", label: "Add (+)" }
			: { bg: "#FEF2F2", color: "#B91C1C", label: "Subtract (-)" };

	const getVisibilityTypeLabel = (item: PartnerBalanceAdjustment) =>
		item.visibilityScope === "admin" ? "Admin" : "Station";

	return (
		<DialogWrapMedium
			login={true}
			open={props.openDialog}
			onClose={props.onClose}
			title={`Financial Config: ${props.partner?.name || ""}`}
			actions={
				<>
					<UserFormServiceButtonClose onClick={props.onClose}>
						Cancel
					</UserFormServiceButtonClose>
					<UserFormServiceButtonCreated disabled={!canSave} onClick={handleSave}>
						{isSaving ? "Saving..." : "Save Changes"}
					</UserFormServiceButtonCreated>
				</>
			}>
			<div style={{ maxHeight: "75vh", overflowY: "auto", paddingRight: "8px" }}>
				<div
					style={{
						background: "#F6F7FB",
						padding: "18px",
						borderRadius: "14px",
						marginBottom: "20px",
						border: "1px solid #E5E7EB",
					}}
				>
					<div
						style={{
							fontWeight: 700,
							marginBottom: "14px",
							borderBottom: "1px solid #E5E7EB",
							paddingBottom: "10px",
						}}
					>
						Current Balance Summary
					</div>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
							gap: "12px",
							marginBottom: "16px",
						}}
					>
						<div
							style={{
								background: "#FFFFFF",
								border: "1px solid #D1FAE5",
								borderRadius: "12px",
								padding: "14px",
							}}
						>
							<div style={{ fontSize: "12px", color: "#6B7280", marginBottom: "6px" }}>Current Available</div>
							<div
								style={{
									fontWeight: 800,
									color: currentAvailable < 0 ? "#DC2626" : "#059669",
									fontSize: "24px",
									lineHeight: 1.2,
								}}
							>
								{formatSummaryMoney(currentAvailable)}
							</div>
							<div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>{summaryCurrency}</div>
						</div>

						<div
							style={{
								background: "#FFFFFF",
								border: "1px solid #DBEAFE",
								borderRadius: "12px",
								padding: "14px",
							}}
						>
							<div style={{ fontSize: "12px", color: "#6B7280", marginBottom: "6px" }}>Paid Amount</div>
							<div style={{ fontWeight: 700, color: "#1D4ED8", fontSize: "22px", lineHeight: 1.2 }}>
								{formatSummaryMoney(paidAmountDisplay)}
							</div>
							<div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>{summaryCurrency}</div>
						</div>

						<div
							style={{
								background: "#FFFFFF",
								border: "1px solid #E5E7EB",
								borderRadius: "12px",
								padding: "14px",
							}}
						>
							<div style={{ fontSize: "12px", color: "#6B7280", marginBottom: "6px" }}>Pending Adjustment</div>
							<div
								style={{
									fontWeight: 700,
									color: pendingAdjustment > 0 ? "#DC2626" : "#7C3AED",
									fontSize: "22px",
									lineHeight: 1.2,
								}}
							>
								{pendingAdjustment > 0 ? "-" : "+"}
								{formatSummaryMoney(Math.abs(pendingAdjustment))}
							</div>
							<div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>{summaryCurrency}</div>
						</div>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-start",
							flexWrap: "wrap",
							gap: "12px",
							marginBottom: "10px",
							fontSize: "12px",
							color: "#6B7280",
						}}
					>
						<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
							<div>
								In Transit:{" "}
								<b style={{ color: "#111827" }}>
									{formatSummaryMoney(inTransitAmount)} {summaryCurrency}
								</b>
							</div>
							{withdrawableAfterReserve != null &&
							reservedInAvailable != null &&
							!Number.isNaN(withdrawableAfterReserve) &&
							!Number.isNaN(reservedInAvailable) ? (
								<>
									<div style={{ color: "#B45309", fontWeight: 600 }}>
										Withdrawable after reserve:{" "}
										{formatSummaryMoney(withdrawableAfterReserve)} {summaryCurrency}
									</div>
									<div style={{ color: "#92400E" }}>
										Reserved (non-withdrawable):{" "}
										<b style={{ color: "#111827" }}>
											{formatSummaryMoney(reservedInAvailable)} {summaryCurrency}
										</b>
									</div>
								</>
							) : null}
						</div>
						<div>
							Projected Available:{" "}
							<b style={{ color: projectedAvailable < 0 ? "#DC2626" : "#059669" }}>
								{formatSummaryMoney(projectedAvailable)} {summaryCurrency}
							</b>
						</div>
					</div>

					<div style={{ marginTop: "12px", paddingTop: "14px", borderTop: "1px solid #E5E7EB" }}>
						<div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "10px", color: "#111827" }}>
							Adjust Balance (Manual)
						</div>
						<div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
							<div style={{ display: "flex", gap: "8px", minWidth: "164px" }}>
								<button
									type="button"
									onClick={() => setAdjustType("add")}
									style={{
										flex: 1,
										borderRadius: "10px",
										border: adjustType === "add" ? "1px solid #34D399" : "1px solid #D1D5DB",
										background: adjustType === "add" ? "#ECFDF5" : "#FFFFFF",
										color: adjustType === "add" ? "#047857" : "#374151",
										fontWeight: 700,
										cursor: "pointer",
									}}
								>
									Add (+)
								</button>
								<button
									type="button"
									onClick={() => setAdjustType("subtract")}
									style={{
										flex: 1,
										borderRadius: "10px",
										border: adjustType === "subtract" ? "1px solid #FCA5A5" : "1px solid #D1D5DB",
										background: adjustType === "subtract" ? "#FEF2F2" : "#FFFFFF",
										color: adjustType === "subtract" ? "#B91C1C" : "#374151",
										fontWeight: 700,
										cursor: "pointer",
									}}
								>
									Subtract (-)
								</button>
							</div>
							<FomrmLoginInput
								style={{ margin: 0, padding: "6px 12px", flex: 1 }}
								placeholder={`Amount in ${summaryCurrency}`}
								value={adjustAmount}
								onChange={(e) => setAdjustAmount((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
							/>
							<button
								type="button"
								onClick={handleAdjust}
								disabled={!canApplyAdjustment}
								style={{
									padding: "0 18px",
									background: canApplyAdjustment ? "#2563EB" : "#93C5FD",
									color: "white",
									border: "none",
									borderRadius: "10px",
									cursor: canApplyAdjustment ? "pointer" : "not-allowed",
									fontWeight: 700,
									minWidth: "92px",
								}}
							>
								Apply
							</button>
						</div>
						<div style={{ marginTop: "10px" }}>
							<label
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: "8px",
									fontSize: 13,
									color: "#374151",
									fontWeight: 600,
									cursor: "pointer",
								}}
							>
								<input
									type="checkbox"
									checked={hideFromStation}
									onChange={(e) => setHideFromStation(e.target.checked)}
									style={{ width: "16px", height: "16px", cursor: "pointer" }}
								/>
								Do not show up on Station account
							</label>
						</div>

						<div style={{ marginTop: "12px" }}>
							<div style={{ fontSize: "12px", fontWeight: 700, color: "#111827", marginBottom: "6px" }}>
								Reason for Adjustment *
							</div>
							<textarea
								value={adjustReason}
								onChange={(e) => setAdjustReason(e.target.value)}
								placeholder="Example: Booking refund correction, manual payout correction, system calculation error"
								rows={3}
								style={{
									width: "100%",
									borderRadius: "10px",
									border: adjustReason.trim() ? "1px solid #D1D5DB" : "1px solid #FCA5A5",
									padding: "10px 12px",
									resize: "vertical",
									fontFamily: "inherit",
									fontSize: "13px",
									boxSizing: "border-box",
								}}
							/>
							<div
								style={{
									marginTop: "6px",
									fontSize: "11px",
									color: adjustReason.trim() ? "#6B7280" : "#B91C1C",
								}}
							>
								{adjustReason.trim()
									? hideFromStation
										? "This adjustment is admin-only: station account and station email will not receive this item."
										: "This reason will be shown in the station balance adjustment history."
									: "Reason is required."}
							</div>
						</div>
						<div style={{ marginTop: "12px" }}>
							<div style={{ fontSize: "12px", fontWeight: 700, color: "#111827", marginBottom: "6px" }}>
								Supporting Files
							</div>
							<div
								style={{
									border: "1px dashed #D1D5DB",
									borderRadius: "10px",
									padding: "12px",
									background: "#FFFFFF",
								}}
							>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
									<div style={{ fontSize: "12px", color: "#6B7280" }}>
										Upload documents, images, or proof files. Max {MAX_ATTACHMENT_FILES} files, {(MAX_ATTACHMENT_SIZE / 1024 / 1024).toFixed(0)} MB each.
									</div>
									<label
										style={{
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											padding: "8px 12px",
											borderRadius: "8px",
											border: "1px solid #D1D5DB",
											background: "#F9FAFB",
											color: "#111827",
											fontSize: "12px",
											fontWeight: 700,
											cursor: adjustFiles.length >= MAX_ATTACHMENT_FILES ? "not-allowed" : "pointer",
											opacity: adjustFiles.length >= MAX_ATTACHMENT_FILES ? 0.6 : 1,
										}}
									>
										Add Files
										<input
											type="file"
											multiple
											onChange={handleAdjustmentFileChange}
											style={{ display: "none" }}
											disabled={adjustFiles.length >= MAX_ATTACHMENT_FILES}
										/>
									</label>
								</div>
								{adjustFiles.length > 0 && (
									<div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
										{adjustFiles.map((file, index) => (
											<div
												key={`${file.name}-${file.size}-${index}`}
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													gap: "12px",
													padding: "10px 12px",
													borderRadius: "8px",
													background: "#F9FAFB",
												}}
											>
												<div style={{ minWidth: 0 }}>
													<div
														style={{
															fontSize: "12px",
															fontWeight: 600,
															color: "#111827",
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}}
														title={file.name}
													>
														{file.name}
													</div>
													<div style={{ fontSize: "11px", color: "#6B7280", marginTop: "2px" }}>
														{formatFileSize(file.size)}
													</div>
												</div>
												<button
													type="button"
													onClick={() => removeAdjustmentFile(index)}
													style={{
														border: "none",
														background: "transparent",
														color: "#B91C1C",
														fontSize: "12px",
														fontWeight: 700,
														cursor: "pointer",
													}}
												>
													Remove
												</button>
											</div>
										))}
									</div>
								)}
								{adjustFilesError && (
									<div style={{ marginTop: "8px", fontSize: "11px", color: "#B91C1C" }}>
										{adjustFilesError}
									</div>
								)}
							</div>
						</div>

						<div style={{ marginTop: "8px", fontSize: "11px", color: "#6B7280" }}>
							Subtract can push available balance below zero when the station has outstanding debt.
						</div>
					</div>
				</div>

				<div
					style={{
						padding: "12px",
						background: "#f5f5f5",
						borderRadius: "8px",
						fontSize: "12px",
						color: "#666",
						marginBottom: "16px",
					}}
				>
					<b>Number format:</b>
					<br />
					Use <b>,</b> for thousand separators and <b>.</b> for decimals.
					<br />
					Example: <b>50,000.00</b> or <b>1,234,567.89</b>
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
					<div>
						<InputOrderAdminTitle>STT Com</InputOrderAdminTitle>
						<FomrmLoginInput
							placeholder="Example: 50,000.00"
							type="text"
							value={stationCom}
							onChange={(e) => setStationCom((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
						/>
						{errors.stationCom && <ErrorNotiLogin>{errors.stationCom}</ErrorNotiLogin>}
					</div>

					<div>
						<InputOrderAdminTitle>Public Rate</InputOrderAdminTitle>
						<FomrmLoginInput
							placeholder="Example: 4,75"
							type="text"
							value={pubRate}
							onChange={(e) => setPubRate((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
						/>
						{errors.pubRate && <ErrorNotiLogin>{errors.pubRate}</ErrorNotiLogin>}
					</div>

					<div>
						<InputOrderAdminTitle>Min Payout (USD)</InputOrderAdminTitle>
						<FomrmLoginInput
							placeholder="Blank = default"
							type="text"
							value={minimumPayout}
							onChange={(e) => setMinimumPayout((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
						/>
						{errors.minimumPayout && <ErrorNotiLogin>{errors.minimumPayout}</ErrorNotiLogin>}
						<div style={{ fontSize: "11px", color: "#6B7280", marginTop: "6px", lineHeight: 1.4 }}>
							Free-withdrawal threshold. Blank = Country Rates / System Config.
							{Number(availableSummary?.minimumPayout) > 0 && (
								<>
									{" "}
									Now: <b>{formatSummaryMoney(availableSummary.minimumPayout)} {summaryCurrency}</b>
								</>
							)}
						</div>
					</div>

					<div>
						<InputOrderAdminTitle>Max Payout (USD)</InputOrderAdminTitle>
						<FomrmLoginInput
							placeholder="Blank = default"
							type="text"
							value={maximumPayout}
							onChange={(e) => setMaximumPayout((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
						/>
						{errors.maximumPayout && <ErrorNotiLogin>{errors.maximumPayout}</ErrorNotiLogin>}
						<div style={{ fontSize: "11px", color: "#6B7280", marginTop: "6px", lineHeight: 1.4 }}>
							Not applied yet — auto payout uses System Config Max Payout.
						</div>
					</div>

					<div style={{ gridColumn: "1 / -1", marginBottom: "8px" }}>
						<FormControlLabel
							style={{ margin: 0 }}
							control={
								<Switch
									color="primary"
									checked={minimumPayoutEnabled}
									onChange={(e) => setMinimumPayoutEnabled(e.target.checked)}
								/>
							}
							label={
								<span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>
									Allow withdrawal below Min Payout (station pays fee)
								</span>
							}
						/>
						<div style={{ fontSize: "11px", color: "#6B7280", marginTop: "2px", lineHeight: 1.4 }}>
							Off = station cannot withdraw until balance reaches Min Payout.
						</div>
					</div>

					<div style={{ gridColumn: "1 / -1", marginBottom: "8px" }}>
						<FormControlLabel
							style={{ margin: 0 }}
							control={
								<Switch
									color="primary"
									checked={minBalanceEnabled}
									onChange={(e) => setMinBalanceEnabled(e.target.checked)}
								/>
							}
							label={
								<span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>
									Apply minimum balance reserve (USD amount below)
								</span>
							}
						/>
					</div>

					<div>
						<InputOrderAdminTitle>Min Balance Reserve (USD)</InputOrderAdminTitle>
						<FomrmLoginInput
							placeholder="Blank = no reserve"
							type="text"
							value={minBalance}
							onChange={(e) => setMinBalance((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
						/>
						{errors.minBalance && <ErrorNotiLogin>{errors.minBalance}</ErrorNotiLogin>}
						<div style={{ fontSize: "11px", color: "#6B7280", marginTop: "6px", lineHeight: 1.4 }}>
							Partner cannot withdraw this amount from available balance; converted to station local currency using the same
							rate as minimum payout.
						</div>
					</div>
				</div>

				<div
					style={{
						background: "#FFFFFF",
						border: "1px solid #E5E7EB",
						borderRadius: "14px",
						padding: "18px",
						marginTop: "20px",
					}}
				>
					<div
						style={{
							fontWeight: 700,
							marginBottom: "14px",
							borderBottom: "1px solid #E5E7EB",
							paddingBottom: "10px",
						}}
					>
						Balance Adjustment History
					</div>
					<div style={{ overflowX: "auto" }}>
						<div style={{ minWidth: "860px" }}>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "140px 80px 100px 110px 120px 120px minmax(180px, 1fr) 120px",
									gap: "12px",
									padding: "10px 12px",
									fontSize: "11px",
									fontWeight: 700,
									color: "#6B7280",
									textTransform: "uppercase",
									borderBottom: "1px solid #E5E7EB",
								}}
							>
								<div>Date</div>
								<div>Visible</div>
								<div>Type</div>
								<div>Amount</div>
								<div>Balance Before</div>
								<div>Balance After</div>
								<div>Reason</div>
								<div>Updated By</div>
							</div>
							{historyLoading ? (
								<div style={{ padding: "18px 12px", color: "#6B7280", fontSize: "13px" }}>
									Loading history...
								</div>
							) : adjustHistory.length === 0 ? (
								<div style={{ padding: "18px 12px", color: "#6B7280", fontSize: "13px" }}>
									No manual balance adjustments yet.
								</div>
							) : (
								adjustHistory.map((item) => {
									const operationStyle = getOperationStyles(item.operation);
									const rowCurrency = item.currencyCode || summaryCurrency;
									return (
										<div
											key={item.id}
											style={{
												display: "grid",
												gridTemplateColumns: "140px 80px 100px 110px 120px 120px minmax(180px, 1fr) 120px",
												gap: "12px",
												padding: "12px",
												fontSize: "13px",
												color: "#111827",
												borderBottom: "1px solid #F3F4F6",
												alignItems: "start",
											}}
										>
											<div>{formatHistoryDate(item.createdAt)}</div>
											<div>{getVisibilityTypeLabel(item)}</div>
											<div>
												<span
													style={{
														display: "inline-flex",
														padding: "4px 8px",
														borderRadius: "999px",
														background: operationStyle.bg,
														color: operationStyle.color,
														fontWeight: 700,
													}}
												>
													{operationStyle.label}
												</span>
											</div>
											<div>
												{formatSummaryMoney(item.amount)} {rowCurrency}
											</div>
											<div>
												{formatSummaryMoney(item.balanceBefore)} {rowCurrency}
											</div>
											<div>
												{formatSummaryMoney(item.balanceAfter)} {rowCurrency}
											</div>
											<div
												style={{
													width: "100%",
													display: "flex",
													flexDirection: "column",
													alignItems: "flex-start",
													whiteSpace: "pre-wrap",
													lineHeight: 1.5,
												}}
											>
												<div>{item.reason}</div>
												{item.attachments && item.attachments.length > 0 && (
													<div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
														{item.attachments.map((attachmentUrl, index) => (
															<PrivateFileAnchor
																key={`${item.id}-${attachmentUrl}-${index}`}
																path={attachmentUrl}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																	padding: "6px 10px",
																	borderRadius: "999px",
																	background: "#EFF6FF",
																	border: "1px solid #BFDBFE",
																	color: "#1D4ED8",
																	fontSize: "12px",
																	fontWeight: 600,
																	textDecoration: "none",
																	lineHeight: 1.2,
																}}
															>
																{getAttachmentDisplayLabel(attachmentUrl, index)}
															</PrivateFileAnchor>
														))}
													</div>
												)}
											</div>
											<div style={{ whiteSpace: "nowrap" }}>
												{item.changedByUser?.fullName || item.changedByUser?.email || "Admin"}
											</div>
										</div>
									);
								})
							)}
						</div>
					</div>
				</div>
			</div>
		</DialogWrapMedium>
	);
}
